<template>
  <div>
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular indeterminate color="primary" size="28" />
    </div>
    <div v-else-if="!items.length" class="pa-4 text--secondary text-center">
      {{ $t('solicitacoesRelatorios.empty') }}
    </div>
    <v-list v-else dense :class="listClass">
      <v-list-item
        v-for="item in items"
        :key="item.id_processo"
        class="solicitacao-item"
      >
        <v-list-item-content>
          <v-list-item-title class="text-body-2">
            {{ displayTitle(item) }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ displaySubtitle(item) }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action class="solicitacao-actions flex-row align-center">
          <v-chip
            x-small
            label
            dark
            :color="statusColor(item)"
            class="mr-2"
          >
            {{ statusText(item) }}
          </v-chip>
          <v-btn
            v-if="item.label === 'ENVIA_EMAIL'"
            icon
            small
            :color="item.canViewLog ? 'primary' : 'grey'"
            :disabled="!item.canViewLog"
            :title="$t('solicitacoesRelatorios.viewLog')"
            @click.stop="openEmailLog(item)"
          >
            <v-icon small>mdi-text-box-outline</v-icon>
          </v-btn>
          <v-btn
            v-else
            icon
            small
            :color="item.canDownload ? 'green darken-2' : 'grey'"
            :disabled="!item.canDownload || downloadingId === item.id_processo"
            :loading="downloadingId === item.id_processo"
            :title="downloadTitle(item)"
            @click.stop="onDownload(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-dialog v-model="logDialog" max-width="560" scrollable>
      <v-card v-if="logItem">
        <v-card-title class="subtitle-1">
          {{ $t('solicitacoesRelatorios.emailLog.title') }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="mb-2">{{ logItem.emailResumo && logItem.emailResumo.mensagem }}</div>
          <div class="text--secondary text-body-2 mb-1">
            {{ $t('solicitacoesRelatorios.emailLog.tool') }}:
            {{ (logItem.emailResumo && logItem.emailResumo.email_tool) || '—' }}
          </div>
          <div class="text--secondary text-body-2 mb-3">
            {{ $t('solicitacoesRelatorios.emailLog.summary', {
              enviados: (logItem.emailResumo && logItem.emailResumo.enviados) || 0,
              falhas: (logItem.emailResumo && logItem.emailResumo.falhas) || 0,
              total: (logItem.emailResumo && logItem.emailResumo.total) || 0,
            }) }}
          </div>
          <div v-if="emailFalhas.length" class="mb-1 font-weight-medium">
            {{ $t('solicitacoesRelatorios.emailLog.failures') }}
          </div>
          <v-list v-if="emailFalhas.length" dense class="pa-0">
            <v-list-item v-for="(f, idx) in emailFalhas" :key="idx" class="px-0">
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ f.to }}</v-list-item-title>
                <v-list-item-subtitle>{{ f.erro || '—' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="text--secondary">
            {{ $t('solicitacoesRelatorios.emailLog.noFailures') }}
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="logDialog = false">
            {{ $t('button.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import {
  SCHEDULER_DOWNLOAD_TYPES,
  buildSchedulerDownloadRequestOptions,
} from '@/utils/schedulerDownloadPending';

const LABEL_I18N = {
  EXPORT_TABLE_CSV: 'solicitacoesRelatorios.labels.tableCsv',
  EXPORT_TABLE_PDF: 'solicitacoesRelatorios.labels.tablePdf',
  EXPORT_SYSTEM_LOG: 'solicitacoesRelatorios.labels.systemLog',
  ENVIA_EMAIL: 'solicitacoesRelatorios.labels.emailSend',
};

const ORIGEM_I18N = {
  mensagens: 'solicitacoesRelatorios.emailOrigem.mensagens',
  credenciais: 'solicitacoesRelatorios.emailOrigem.credenciais',
  recupera_senha: 'solicitacoesRelatorios.emailOrigem.recuperaSenha',
};

const STATUS_I18N = {
  PENDING: 'solicitacoesRelatorios.status.pending',
  EXECUTING: 'solicitacoesRelatorios.status.executing',
  DONE: 'solicitacoesRelatorios.status.done',
  FAILED: 'solicitacoesRelatorios.status.failed',
  CANCELED: 'solicitacoesRelatorios.status.canceled',
};

export default {
  name: 'SolicitacoesRelatoriosList',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      downloadingId: null,
      logDialog: false,
      logItem: null,
    };
  },
  computed: {
    listClass() {
      return this.dense ? 'solicitacoes-list--dense pa-0' : 'pa-0';
    },
    emailFalhas() {
      const detalhes =
        this.logItem &&
        this.logItem.emailResumo &&
        Array.isArray(this.logItem.emailResumo.detalhes)
          ? this.logItem.emailResumo.detalhes
          : [];
      return detalhes.filter((d) => d && d.ok === false);
    },
  },
  methods: {
    labelText(item) {
      const key = LABEL_I18N[item.label];
      return key ? this.$t(key) : item.label;
    },
    origemText(item) {
      if (!item) return '';
      if (item.label === 'ENVIA_EMAIL' && item.origem) {
        const key = ORIGEM_I18N[item.origem];
        return key ? this.$t(key) : item.origem;
      }
      if (item.origem) return item.origem;
      if (item.label === 'EXPORT_SYSTEM_LOG' && (item.dti || item.dtf)) {
        const dti = item.dti || '—';
        const dtf = item.dtf || '—';
        return `${dti} → ${dtf}`;
      }
      return '';
    },
    displayTitle(item) {
      return this.origemText(item) || this.labelText(item);
    },
    displaySubtitle(item) {
      const parts = [];
      if (this.origemText(item)) {
        parts.push(this.labelText(item));
      }
      if (
        item &&
        item.label === 'ENVIA_EMAIL' &&
        item.emailResumo &&
        item.emailResumo.total != null
      ) {
        parts.push(
          this.$t('solicitacoesRelatorios.emailLog.summary', {
            enviados: item.emailResumo.enviados || 0,
            falhas: item.emailResumo.falhas || 0,
            total: item.emailResumo.total || 0,
          })
        );
      }
      if (item && item.dt_requisicao_extenso) {
        parts.push(item.dt_requisicao_extenso);
      }
      return parts.join(' · ');
    },
    statusText(item) {
      if (item && item.exportErro) {
        return this.$t('solicitacoesRelatorios.status.failed');
      }
      if (
        item &&
        item.status === 'DONE' &&
        item.arquivo &&
        item.arquivoDisponivel === false &&
        !item.exportErro
      ) {
        return this.$t('solicitacoesRelatorios.status.expired');
      }
      if (
        item &&
        item.label === 'ENVIA_EMAIL' &&
        item.status === 'DONE' &&
        item.emailResumo &&
        item.emailResumo.falhas > 0
      ) {
        return this.$t('solicitacoesRelatorios.status.donePartial');
      }
      const key = STATUS_I18N[item && item.status];
      return key ? this.$t(key) : (item && item.status) || '';
    },
    statusColor(item) {
      if (item && item.exportErro) {
        return 'red darken-2';
      }
      if (
        item &&
        item.status === 'DONE' &&
        item.arquivo &&
        item.arquivoDisponivel === false &&
        !item.exportErro
      ) {
        return 'grey darken-1';
      }
      if (
        item &&
        item.label === 'ENVIA_EMAIL' &&
        item.status === 'DONE' &&
        item.emailResumo &&
        item.emailResumo.falhas > 0
      ) {
        return 'orange darken-2';
      }
      const status = item && item.status;
      if (status === 'DONE') return 'green darken-2';
      if (status === 'FAILED' || status === 'CANCELED') return 'red darken-2';
      if (status === 'EXECUTING') return 'orange darken-2';
      return 'blue darken-2';
    },
    downloadTitle(item) {
      if (item && item.exportErro) {
        return item.exportMensagem || this.$t('solicitacoesRelatorios.status.failed');
      }
      if (
        item &&
        item.status === 'DONE' &&
        item.arquivo &&
        item.arquivoDisponivel === false
      ) {
        return this.$t('solicitacoesRelatorios.exportExpired');
      }
      return this.$t('solicitacoesRelatorios.download');
    },
    openEmailLog(item) {
      if (!item || !item.canViewLog) return;
      this.logItem = item;
      this.logDialog = true;
    },
    onDownload(item) {
      if (!item || !item.downloadType) return;
      if (!item.canDownload) {
        if (item.status === 'DONE' && item.arquivoDisponivel === false) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('solicitacoesRelatorios.exportExpired'),
            tipo: 'error',
          });
        }
        return;
      }
      const options = buildSchedulerDownloadRequestOptions(
        item.downloadType,
        item.id_processo,
        (key) => this.$t(key)
      );
      if (!options) return;

      this.downloadingId = item.id_processo;
      if (
        item.downloadType === SCHEDULER_DOWNLOAD_TYPES.TABLE_CSV_EXPORT ||
        item.downloadType === SCHEDULER_DOWNLOAD_TYPES.TABLE_PDF_EXPORT
      ) {
        apiController.request(apiController.endpoints.exportDownloadTableCsv, options);
      } else if (item.downloadType === SCHEDULER_DOWNLOAD_TYPES.SYSTEM_LOG_EXPORT) {
        apiController.request(apiController.endpoints.auditDownloadSystemlogExport, options);
      } else {
        this.downloadingId = null;
        return;
      }
      setTimeout(() => {
        if (this.downloadingId === item.id_processo) {
          this.downloadingId = null;
        }
      }, 1500);
    },
  },
};
</script>

<style scoped>
.solicitacao-actions {
  margin: 0 !important;
  align-self: center;
}
.solicitacoes-list--dense .solicitacao-item {
  min-height: 48px;
}
</style>
